import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase.js';

export let currentUserRole = 'Representative'; // Cache active user role
window.isRegistering = false;

/**
 * Login user using Email and Password
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<UserCredential>}
 */
export async function loginUser(email, password) {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    // Fetch and cache role immediately upon login (which checks if the Firestore document exists)
    currentUserRole = await getUserRole(cred.user.uid, email);
    return cred;
  } catch (error) {
    // If the demo accounts are not yet created in the user's Firebase Console, auto-create them
    if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
      const demoUsers = ['admin@aurapharma.com', 'rep@aurapharma.com'];
      if (demoUsers.includes(email.toLowerCase())) {
        try {
          console.log(`Auto-registering demo credential: ${email}`);
          const cred = await createUserWithEmailAndPassword(auth, email, password);
          currentUserRole = await getUserRole(cred.user.uid, email);
          return cred;
        } catch (createError) {
          console.error("Auto-registration error:", createError);
        }
      }
    }
    console.error("Error signing in:", error);
    throw error;
  }
}


/**
 * Sign out the currently authenticated user
 * @returns {Promise<void>}
 */
export async function logoutUser() {
  try {
    currentUserRole = 'Representative'; // Reset cache
    localStorage.removeItem('pharma-user-role'); // Clear cached role on logout
    return await signOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
}

/**
 * Observe Authentication changes and fetch roles dynamically
 * @param {function} callback 
 * @returns {import('firebase/auth').Unsubscribe}
 */
export function checkAuthState(callback) {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      currentUserRole = await getUserRole(user.uid, user.email);
    } else {
      currentUserRole = 'Representative';
    }
    callback(user);
  });
}

/**
 * Get current authenticated user object
 * @returns {import('firebase/auth').User | null}
 */
export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Retrieve User Role from Firestore users collection with optimistic local caching
 * @param {string} uid 
 * @param {string} email 
 * @returns {Promise<string>} - 'Admin' | 'Manager' | 'Representative' | 'Viewer'
 */
export async function getUserRole(uid, email) {
  // 1. Try reading from localStorage first for instant startup
  try {
    const cachedRole = localStorage.getItem('pharma-user-role');
    if (cachedRole) {
      // Trigger background update to ensure it's fresh, but return cache immediately
      updateUserRoleCacheInBackground(uid, email);
      return cachedRole;
    }
  } catch (e) {
    console.warn("Error reading role from localStorage:", e);
  }

  // 2. Fallback to direct fetch if not cached
  return await fetchUserRoleFromDb(uid, email);
}

/**
 * Fetch latest user role from db and cache it
 */
async function fetchUserRoleFromDb(uid, email) {
  try {
    if (window.isRegistering) {
      console.log("Registration in progress, waiting for user document...");
      while (window.isRegistering) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    const userRef = doc(db, 'users', uid);
    
    // Set a 2-second timeout to prevent loading hangs when offline
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error("Timeout")), 2000)
    );
    
    const snap = await Promise.race([
      getDoc(userRef),
      timeoutPromise
    ]);
    
    let role = 'Representative';
    if (snap && snap.exists()) {
      const data = snap.data();
      if (data.status === 'Pending_Approval') {
        if (email.toLowerCase() === 'admin@aurapharma.com') {
          // Auto-approve demo admin user
          await setDoc(userRef, { status: 'Approved' }, { merge: true });
          role = 'Admin';
        } else {
          await signOut(auth);
          localStorage.removeItem('pharma-user-role');
          const err = new Error("PENDING_APPROVAL");
          err.code = "auth/user-pending";
          throw err;
        }
      } else {
        role = data.role || 'Representative';
      }
    } else {
      // Allow auto-registration ONLY for demo users
      const isDemoUser = ['admin@aurapharma.com', 'rep@aurapharma.com'].includes(email.toLowerCase());
      if (isDemoUser) {
        let assignedRole = 'Representative';
        if (email.startsWith('admin')) assignedRole = 'Admin';

        let status = 'Pending_Approval';
        if (assignedRole === 'Admin' || email.toLowerCase() === 'admin@aurapharma.com') {
          status = 'Approved';
        }

        const payload = {
          email,
          role: assignedRole,
          status,
          createdDate: new Date().toISOString()
        };
        
        await setDoc(userRef, payload);
        
        if (status === 'Pending_Approval') {
          await signOut(auth);
          localStorage.removeItem('pharma-user-role');
          const err = new Error("PENDING_APPROVAL");
          err.code = "auth/user-pending";
          throw err;
        }
        
        role = assignedRole;
      } else {
        // Real user with no Firestore document -> Signed up but never verified OTP!
        await signOut(auth);
        localStorage.removeItem('pharma-user-role');
        const err = new Error("EMAIL_UNVERIFIED");
        err.code = "auth/email-not-verified";
        throw err;
      }
    }
    
    // Save to cache
    localStorage.setItem('pharma-user-role', role);
    return role;
  } catch (err) {
    if (err.code === 'auth/user-pending' || err.code === 'auth/email-not-verified') throw err;
    console.warn("Failed to fetch user role from db, returning default:", err);
    return localStorage.getItem('pharma-user-role') || 'Representative';
  }
}

/**
 * Update user role cache silently in the background
 */
async function updateUserRoleCacheInBackground(uid, email) {
  try {
    const userRef = doc(db, 'users', uid);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data.status === 'Pending_Approval') {
        await signOut(auth);
        localStorage.removeItem('pharma-user-role');
        return;
      }
      const role = data.role || 'Representative';
      localStorage.setItem('pharma-user-role', role);
    }
  } catch (e) {
    console.debug("Background role check skipped:", e);
  }
}

/**
 * Send password reset recovery link
 * @param {string} email 
 * @returns {Promise<void>}
 */
export async function sendPasswordReset(email) {
  return await sendPasswordResetEmail(auth, email);
}

// EmailJS Configuration for sending OTP verification codes
export const EMAILJS_CONFIG = {
  serviceId: 'service_aura_otp',
  templateId: 'template_aura_otp',
  publicKey: 'user_aura_otp'
};

/**
 * Send an OTP code to a user's email via EmailJS
 * @param {string} email
 * @param {string} otp
 * @returns {Promise<boolean>}
 */
export async function sendOtpEmail(email, otp) {
  try {
    let serviceId = EMAILJS_CONFIG.serviceId;
    let templateId = EMAILJS_CONFIG.templateId;
    let publicKey = EMAILJS_CONFIG.publicKey;

    // Load custom settings dynamically from Firestore if configured
    try {
      const docRef = doc(db, 'settings', 'email_otp');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.serviceId && data.templateId && data.publicKey) {
          serviceId = data.serviceId;
          templateId = data.templateId;
          publicKey = data.publicKey;
          console.log("Using custom EmailJS configurations from Firestore settings");
        }
      }
    } catch (dbErr) {
      console.warn("Failed to load custom OTP settings, using defaults:", dbErr);
    }

    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        service_id: serviceId,
        template_id: templateId,
        user_id: publicKey,
        template_params: {
          email: email,
          passcode: otp,
          time: '5 minutes',
          company_name: 'Urena Healthcare'
        }
      })
    });
    
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`EmailJS error: ${errText}`);
    }
    
    console.log(`OTP verification email sent to ${email}`);
    return true;
  } catch (err) {
    console.warn("Failed to send OTP email via EmailJS:", err);
    throw err;
  }
}

/**
 * Register a new user in Firebase Auth (verification handled via custom OTP)
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<UserCredential>}
 */
export async function registerUser(email, password) {
  try {
    window.isRegistering = true; // prevent automatic logout in checkAuthState
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return cred;
  } catch (err) {
    window.isRegistering = false;
    console.error("registerUser Error:", err);
    throw err;
  }
}

/**
 * Completes registration for an email-verified user by creating their document in Firestore.
 * @param {import('firebase/auth').User} user
 * @param {string} role - 'Admin' | 'Manager' | 'Representative' | 'Viewer'
 * @returns {Promise<string>} - 'Approved' | 'Pending_Approval'
 */
export async function completeUserRegistration(user, role) {
  try {
    window.isRegistering = true;
    
    // Query the users collection for any approved/legacy Administrators
    const usersColl = collection(db, 'users');
    const adminQuery = query(usersColl, where('role', '==', 'Admin'));
    const adminSnapshot = await getDocs(adminQuery);
    
    let hasActiveAdmin = false;
    adminSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.status === 'Approved' || !data.status) {
        hasActiveAdmin = true;
      }
    });

    let status = 'Pending_Approval';
    if (role === 'Admin' && !hasActiveAdmin) {
      status = 'Approved';
    }

    // Write user document to Firestore
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      email: user.email,
      role,
      status,
      createdDate: new Date().toISOString()
    });
    
    currentUserRole = role;
    window.isRegistering = false;
    return status;
  } catch (err) {
    window.isRegistering = false;
    console.error("completeUserRegistration Error:", err);
    throw err;
  }
}


